// src/types/training.ts

export enum TrainingType {
  ONE_TO_ONE = "one_to_one",
  GROUP_PERSONAL = "group_personal",
  GROUP_TRAINING = "group_training",
}

export enum TrainingPackageStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

export enum TrainingSeriesStatus {
  ACTIVE = "active",
  COMPLETED = "completed",
  EXPIRED = "expired",
  CANCELLED = "cancelled",
}

export enum TrainingSessionStatus {
  PENDING_SCHEDULE = "pending_schedule",
  BOOKED = "booked",
  COMPLETED = "completed",
  ABSENT = "absent",
  RESCHEDULED = "rescheduled",
  CANCELLED = "cancelled",
}

export interface TrainingPackage {
  id: number;
  org_id: number;
  name: string;
  training_type: TrainingType;
  coach_id: number;
  coach_name?: string;
  facility_id: number;
  facility_name?: string;
  total_credits: number;
  credits_per_session: number;
  slot_duration_hours: number;
  validity_days: number;
  max_capacity: number;
  price: number;
  price_master_id?: number | null;
  price_master_name?: string | null;
  rule_code?: string | null;
  income_category_id?: number | null;
  tax_group_id?: number | null;
  tax_group?: any;
  reschedule_cutoff_hours: number;
  status: TrainingPackageStatus;
  description?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface TrainingPackageCreate {
  name: string;
  training_type: TrainingType;
  coach_id: number;
  facility_id: number;
  total_credits: number;
  credits_per_session?: number;
  slot_duration_hours?: number;
  validity_days?: number;
  max_capacity?: number;
  price: number;
  price_master_id?: number | null;
  income_category_id?: number | null;
  tax_group_id?: number | null;
  reschedule_cutoff_hours?: number;
  status?: TrainingPackageStatus;
  description?: string | null;
}

export interface TrainingPackageUpdate extends Partial<TrainingPackageCreate> {
  id?: number;
}

export interface TrainingSession {
  id: number;
  series_id: number;
  session_number: number;
  facility_booking_id?: number | null;
  schedule_slot_id?: number | null;
  workout_day_id?: number | null;
  session_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  coach_id: number;
  coach_name?: string;
  facility_id: number;
  facility_name?: string;
  status: TrainingSessionStatus;
  attendance_marked_at?: string | null;
  attendance_marked_by?: number | null;
  attendance_marked_by_name?: string | null;
  coach_notes?: string | null;
  credits_consumed: number;
  workout_id?: number | null;
  workout_name?: string | null;
  workout_day?: {
    id: number;
    day_name: string;
    week?: number;
    day?: number;
    exercises?: any[];
  } | null;
  session_exercises?: Array<{
    id?: number;
    exercise_id: number;
    exercise_name?: string | null;
    sequence_number?: number;
    sets?: number;
    repetitions_per_set?: number[];
    weights_per_set?: number[];
    is_completed?: boolean;
    notes?: string | null;
  }>;
  training_package?: TrainingPackage | null;
  training_package_id?: number | null;
  package_name?: string | null;
}

export interface ClientTrainingSeries {
  id: number;
  org_id: number;
  client_id: number;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  training_package_id: number;
  package_name?: string;
  pos_transaction_id?: number | null;
  coach_id: number;
  coach_name?: string;
  facility_id: number;
  facility_name?: string;
  total_credits: number;
  credits_remaining: number;
  total_sessions: number;
  sessions_completed: number;
  valid_from?: string;
  valid_until?: string;
  schedule_start_date?: string | null;
  schedule_end_date?: string | null;
  workout_id?: number | null;
  workout_name?: string | null;
  training_type?: TrainingType | string;
  status: TrainingSeriesStatus;
  sessions?: TrainingSession[];
  created_at?: string;
  created_by?: number | string | null;
  created_by_name?: string | null;
  creator_name?: string | null;
}

export interface GenerateSchedulePayload {
  first_session_date: string; // YYYY-MM-DD
  start_time: string;         // HH:MM:SS
  repeat_type?: "daily" | "weekly";
  day_of_week?: number;        // 0=Mon ... 6=Sun (legacy single weekday)
  days_of_week?: number[];     // 0=Mon ... 6=Sun (multi-day selection; all 7 for daily)
  interval?: number;           // Recurrence interval (e.g. 1=every day/week, 2=skip 1 day/week)
}

export interface ReschedulePayload {
  new_start_time: string;     // ISO format
  notes?: string;
}

export interface GetAllResponse<T> {
  data: T[];
  total_counts: number;
  filtered_counts: number;
}